import { SIGNAL_QUEUE_FILED_FLOOR_DAYS, type SignalPriority, type SignalType } from '@parcel/types';
import { toast } from '@parcel/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from './api.js';
import { invalidateFeed } from './feed.js';
import { isSupabaseConfigured } from './supabase.js';

/** Prefer the API's specific error message over a generic fallback. */
function subscriptionErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const body = err.body as { error?: { message?: unknown } };
    if (typeof body.error?.message === 'string') return body.error.message;
  }
  return fallback;
}

// -- Wire types ---------------------------------------------------------------

export interface ApiSignalSubscription {
  signal_type: SignalType;
  priority: SignalPriority;
  created_at: string;
  updated_at: string;
}

export interface ApiSignalSubscriptionListResponse {
  subscriptions: ApiSignalSubscription[];
  /** The plan's signal history window: 30 free, 90 paid. */
  filed_floor_days: number;
}

// -- UI model (what components consume) ---------------------------------------

export interface SignalSubscription {
  signalType: SignalType;
  priority: SignalPriority;
}

export interface SignalSubscriptionList {
  subscriptions: SignalSubscription[];
  /** The plan's signal history window: 30 free, 90 paid. */
  filedFloorDays: number;
}

export function fromApiSubscription(api: ApiSignalSubscription): SignalSubscription {
  return { signalType: api.signal_type, priority: api.priority };
}

export interface SubscriptionUpsertInput {
  signalType: SignalType;
  priority?: SignalPriority;
}

export function toUpsertBody(
  workspaceId: string,
  input: SubscriptionUpsertInput,
): Record<string, unknown> {
  return {
    workspace_id: workspaceId,
    ...(input.priority !== undefined ? { priority: input.priority } : {}),
  };
}

/** Onboarding bulk body: the selection map's explicit per-type priorities, as wire records. */
export function buildBulkSubscriptionsBody(
  selections: ReadonlyMap<SignalType, SignalPriority>,
): Array<{ signal_type: SignalType; priority: SignalPriority }> {
  return [...selections].map(([signal_type, priority]) => ({ signal_type, priority }));
}

export async function createSignalSubscriptionsBulk(
  workspaceId: string,
  subscriptions: Array<{ signal_type: SignalType; priority: SignalPriority }>,
): Promise<ApiSignalSubscriptionListResponse> {
  // Local preview has no backend — mirror the other workspace stubs so the
  // onboarding Signals step can complete without hitting the API.
  if (!isSupabaseConfigured) {
    return { subscriptions: [], filed_floor_days: SIGNAL_QUEUE_FILED_FLOOR_DAYS };
  }
  return apiFetch<ApiSignalSubscriptionListResponse>('/signal-subscriptions/bulk', {
    method: 'POST',
    body: JSON.stringify({ workspace_id: workspaceId, subscriptions }),
  });
}

// -- Hooks --------------------------------------------------------------------

export function useSignalSubscriptions(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['signal-subscriptions', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<SignalSubscriptionList> => {
      const qs = new URLSearchParams({ workspace_id: workspaceId ?? '' });
      const res = await apiFetch<ApiSignalSubscriptionListResponse>(
        `/signal-subscriptions?${qs.toString()}`,
      );
      return {
        subscriptions: res.subscriptions.map(fromApiSubscription),
        filedFloorDays: res.filed_floor_days,
      };
    },
  });
}

export function useSignalSubscriptionMutations(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['signal-subscriptions', workspaceId] });
    // Subscriptions decide which signals count in the News feed.
    invalidateFeed(queryClient, workspaceId);
  };

  const upsert = useMutation({
    mutationFn: async (input: SubscriptionUpsertInput) => {
      if (!workspaceId) throw new Error('Cannot update signal subscriptions without a workspace');
      return apiFetch<ApiSignalSubscriptionListResponse>(
        `/signal-subscriptions/${encodeURIComponent(input.signalType)}`,
        { method: 'PUT', body: JSON.stringify(toUpsertBody(workspaceId, input)) },
      );
    },
    onError: (err) => {
      console.error('[signals] subscription update failed', err);
      toast.error(
        subscriptionErrorMessage(err, "Couldn't save the subscription — please try again."),
      );
    },
    onSettled: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (signalType: SignalType) => {
      if (!workspaceId) throw new Error('Cannot update signal subscriptions without a workspace');
      const qs = new URLSearchParams({ workspace_id: workspaceId });
      return apiFetch<ApiSignalSubscriptionListResponse>(
        `/signal-subscriptions/${encodeURIComponent(signalType)}?${qs.toString()}`,
        { method: 'DELETE' },
      );
    },
    onError: (err) => {
      console.error('[signals] unsubscribe failed', err);
      toast.error("Couldn't unsubscribe — please try again.");
    },
    onSettled: invalidate,
  });

  return { upsert, remove };
}
